import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../../../components/ui/Button.tsx";
import MultiSelectSearchField from "../../../components/ui/MultiSelectSearchField.tsx";
import Dialog from "../../../components/ui/Dialog";
import Tooltip from "../../../components/ui/Tooltip";
import DisabledTabTooltip from "../../../components/ui/DisabledTabTooltip";
import NameWithEmailTooltip from "../../../components/ui/NameWithEmailTooltip";
import NoActiveCycle from "../../../components/ui/NoActiveCycle";
import { toTitleCase } from "../../../utils/stringUtils.ts";
import { employeeService } from "../../../services";
import { useRBAC } from "../../../hooks/useRBAC";
import { useActiveCycle } from "../../../hooks/useActiveCycle";
import { useAppSelector } from "../../../hooks/redux";
import { CycleType } from "../../../types";
import { getOkrResultColumnLabel } from "../../../utils/cycleUtils";
import type { Employee } from "../../../services/selfAppraisalService";

interface Tab {
  id: "objectives" | "nominations";
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    id: "objectives",
    label: "Objectives and Key Results",

    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
        />
      </svg>
    ),
  },
  {
    id: "nominations",
    label: "Nominations",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
];

interface OKRData {
  id: string;
  objective: string;
  target: string;
  endYearResult: string;
  rating: number | null;
}

interface NominationData {
  nominationApprover: string;
  nominationApproverEmail?: string; // Add email field for tooltip
  superiors: Employee[];
  dotted_superior: Employee[];
  subordinates: Employee[];
  additional_subordinates: Employee[];
  peers: Employee[];
  stakeholders: Employee[];
}

interface SelfAppraisalPageProps {
  okrs: OKRData[];
  nominations: NominationData;
  availableNominations?: {
    superiors: Employee[];
    dotted_superior: Employee[];
    subordinates: Employee[];
    additional_subordinates: Employee[];
    peers: Employee[];
    stakeholders: Employee[];
  };
  onOKRChange: (
    id: string,
    field: keyof OKRData,
    value: string | number | null,
  ) => void;
  onNominationChange: (
    field: keyof NominationData,
    value: string | Employee[],
  ) => void;
  onAddOKR: () => void;
  onRemoveOKR: (id: string) => void;
  onSubmit: () => void; // For OKR submission
  onSubmitNomination?: () => void; // For nomination submission
  onSaveDraftOKR?: () => void | Promise<void>;
  onSaveDraftNomination?: () => void;
  onRefreshObjectives?: () => void;
  onRefreshNominations?: () => void;
  isSubmitting: boolean;
  isSaving?: boolean;
  isEditable?: boolean; // For OKR fields
  isNominationEditable?: boolean; // For nomination fields
  immediateManager?: string;
  immediateManagerEmail?: string; // Add email for tooltip
  daysLeft?: number; // For objectives deadline
  nominationDaysLeft?: number; // For nominations deadline
  isFormValid?: boolean;
  validationErrors?: string[];
  canSaveDraft?: boolean;
  draftExists?: boolean; // Indicates if a draft exists
  isNominationDisabled?: boolean;
  isNominationSubmitEnabled?: boolean; // For enabling nomination submit button based on API data
  isNominationRefreshing?: boolean; // For showing loading state during nomination refresh
  nominationStatus?: string; // Add nomination status prop
  managerComments?: string; // Add manager comments prop for nominations
  okrComments?: string; // Add manager comments prop for OKRs
  okrStatus?: string; // Add OKR status prop (e.g., 'approved', 'rejected', 'pending')
  apiErrorMessage?: string; // Add API error message prop
  apiErrorTitle?: string; // Add API error title prop
  isFreezed?: boolean; // Add freezed status prop
  hasUnsavedChanges?: boolean;
  hasUnsavedNominationChanges?: boolean;
  cycleType?: number; // Phase of the active cycle: START(0), MID(1), END(2)
}

const SelfAppraisalPage: React.FC<SelfAppraisalPageProps> = ({
  okrs,
  nominations,
  availableNominations,
  onOKRChange,
  onNominationChange,
  onAddOKR,
  onRemoveOKR,
  onSubmit,
  onSubmitNomination,
  onSaveDraftOKR,
  onSaveDraftNomination,
  onRefreshObjectives,
  onRefreshNominations,
  isSubmitting,
  isSaving,
  isEditable = true,
  isNominationEditable = true,
  immediateManager,
  immediateManagerEmail,
  isFormValid,
  validationErrors,
  daysLeft: _daysLeft, // eslint-disable-line @typescript-eslint/no-unused-vars
  nominationDaysLeft: _nominationDaysLeft, // eslint-disable-line @typescript-eslint/no-unused-vars
  canSaveDraft,
  draftExists,
  isNominationDisabled = false,
  isNominationSubmitEnabled = false,
  isNominationRefreshing = false,
  nominationStatus,
  managerComments,
  okrComments,
  okrStatus,
  apiErrorMessage,
  apiErrorTitle,
  isFreezed = false,
  hasUnsavedChanges = false,
  hasUnsavedNominationChanges = false,
  cycleType,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = useRBAC();
  const hasActiveCycle = useActiveCycle();

  // Result and Rating columns are locked only during Target Settings.
  const isEyrRatingLocked = cycleType === CycleType.START;
  const resultColumnLabel = getOkrResultColumnLabel(cycleType);

  const eyrRatingLockedTooltip = (() => {
    if (cycleType === CycleType.START) return 'Result and Rating are not applicable during the Target Settings cycle.';
    return 'This field is locked for the current cycle phase.';
  })();

  const hasOKRAccess = hasAccess("self_appraisal_okr");
  const hasNominationAccess = hasAccess("self_appraisal_nomination");
  const tabAccessMap: Record<Tab["id"], boolean> = {
    objectives: hasOKRAccess,
    nominations: hasNominationAccess,
  };

  const defaultTabFromLocation: "objectives" | "nominations" =
    location.pathname.includes("/nominations") ||
    location.pathname.includes("/nomination")
      ? "nominations"
      : "objectives";

  const initialActiveTab: "objectives" | "nominations" = (() => {
    if (defaultTabFromLocation === "nominations") {
      if (hasNominationAccess) return "nominations";
      if (hasOKRAccess) return "objectives";
    } else {
      if (hasOKRAccess) return "objectives";
      if (hasNominationAccess) return "nominations";
    }
    if (hasOKRAccess) return "objectives";
    if (hasNominationAccess) return "nominations";
    return "objectives";
  })();

  const [activeTab, setActiveTab] = useState<"objectives" | "nominations">(
    initialActiveTab,
  );

  // Store manager values in localStorage to persist during refreshes
  const [localImmediateManager, setLocalImmediateManager] = useState(() => {
    try {
      return localStorage.getItem("selfAppraisal_immediateManager") || "";
    } catch {
      return "";
    }
  });

  const [localNominationApprover, setLocalNominationApprover] = useState(() => {
    try {
      return localStorage.getItem("selfAppraisal_nominationApprover") || "";
    } catch {
      return "";
    }
  });

  // Update localStorage when props change
  useEffect(() => {
    if (immediateManager && immediateManager !== localImmediateManager) {
      setLocalImmediateManager(immediateManager);
      try {
        localStorage.setItem(
          "selfAppraisal_immediateManager",
          immediateManager,
        );
      } catch {
        // ignore localStorage errors
      }
    }
  }, [immediateManager, localImmediateManager]);

  useEffect(() => {
    if (
      nominations.nominationApprover &&
      nominations.nominationApprover !== localNominationApprover
    ) {
      setLocalNominationApprover(nominations.nominationApprover);
      try {
        localStorage.setItem(
          "selfAppraisal_nominationApprover",
          nominations.nominationApprover,
        );
      } catch {
        // ignore localStorage errors
      }
    }
  }, [nominations.nominationApprover, localNominationApprover]);

  // Use the stored values as fallback
  const displayImmediateManager = immediateManager || localImmediateManager;
  const displayNominationApprover =
    nominations.nominationApprover || localNominationApprover;
  const displayNominationApproverEmail = nominations.nominationApproverEmail;
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportRemarks, setReportRemarks] = useState("");
  const [reportRemarksError, setReportRemarksError] = useState("");
  const [reportEmployeeEmail, setReportEmployeeEmail] = useState("");
  const [reportManagerEmail, setReportManagerEmail] = useState("");
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);
  const [showReportSuccess, setShowReportSuccess] = useState(false);

  // Get user email from auth context
  const { user } = useAppSelector((state) => state.auth);
  const currentUserEmail = user?.email?.trim().toLowerCase();
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const [isSaveDraftPromptOpen, setIsSaveDraftPromptOpen] = useState(false);
  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [isSavingBeforeSubmit, setIsSavingBeforeSubmit] = useState(false);
  const [isNomSubmitConfirmOpen, setIsNomSubmitConfirmOpen] = useState(false);
  const [isNomReportOpen, setIsNomReportOpen] = useState(false);
  const [nomReportRemarks, setNomReportRemarks] = useState("");
  const [nomReportSelected, setNomReportSelected] = useState<string[]>([]);
  const [nomReportError, setNomReportError] = useState("");
  const [isNomReportSubmitting, setIsNomReportSubmitting] = useState(false);
  const [showNomReportSuccess, setShowNomReportSuccess] = useState(false);
  // Removed isRejectedReasonModalOpen state
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Filter hierarchy options based on available nominations
  const hierarchyOptions = React.useMemo(() => {
    const options: string[] = [];

    // Always include Superiors since it's auto-populated
    if (nominations.superiors.length > 0) {
      options.push("Superiors");
    }

    // Include additional superiors when the API provides them.
    if (availableNominations?.dotted_superior?.length) {
      options.push("Additional Superiors");
    }

    // Only include Subordinates if available
    if (
      availableNominations?.subordinates &&
      availableNominations.subordinates.length > 0
    ) {
      options.push("Subordinates");
    }

    if (availableNominations?.additional_subordinates?.length) {
      options.push("Additional Subordinates");
    }

    // Only include Peers if available
    if (availableNominations?.peers && availableNominations.peers.length > 0) {
      options.push("Peers");
    }

    // Only include Stakeholders if available
    if (
      availableNominations?.stakeholders &&
      availableNominations.stakeholders.length > 0
    ) {
      options.push("Stakeholders");
    }

    return options;
  }, [nominations.superiors, availableNominations]);

  // Auto-resize textareas effect - sync heights within each row
  useEffect(() => {
    okrs.forEach((okr) => {
      const row = document.querySelector(`[data-okr-row="${okr.id}"]`);
      if (!row) return;

      const textareas = row.querySelectorAll(
        'textarea[data-auto-resize="true"]',
      );
      let maxHeight = 48; // minimum height

      // Calculate max height needed
      textareas.forEach((textarea) => {
        const element = textarea as HTMLTextAreaElement;
        element.style.height = "auto";
        const scrollHeight = element.scrollHeight;
        if (scrollHeight > maxHeight) {
          maxHeight = scrollHeight;
        }
      });

      // Apply max height to all textareas in the row
      textareas.forEach((textarea) => {
        const element = textarea as HTMLTextAreaElement;
        element.style.height = maxHeight + "px";
      });
    });
  }, [okrs]); // Re-run when OKR data changes (e.g., after save/load)

  // Helper function to get validation errors for a specific OKR
  const getOkrErrors = (okrIndex: number): string[] => {
    if (!validationErrors || !showValidationErrors) return [];
    const okrNumber = okrIndex + 1;
    return validationErrors.filter((error) => {
      const errorStr = String(error);
      return (
        errorStr.startsWith(`OKR ${okrNumber}:`) ||
        errorStr.includes(`OKR ${okrNumber}`)
      );
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem("selfAppraisal_activeTab", activeTab);
    } catch {
      // ignore
    }
  }, [activeTab]);

  const refreshObjectivesRef = useRef(onRefreshObjectives);
  const refreshNominationsRef = useRef(onRefreshNominations);
  const saveDraftNominationRef = useRef(onSaveDraftNomination);
  refreshObjectivesRef.current = onRefreshObjectives;
  refreshNominationsRef.current = onRefreshNominations;
  saveDraftNominationRef.current = onSaveDraftNomination;

  const handleTabChange = (tabId: "objectives" | "nominations") => {
    if (!tabAccessMap[tabId]) {
      return;
    }

    setActiveTab(tabId);
    // Update URL to nested route
    if (tabId === "objectives") {
      navigate("/self-appraisal/okr-submission");
    } else {
      navigate("/self-appraisal/nominations");
    }

    requestAnimationFrame(() => {
      if (
        tabId === "objectives" &&
        typeof refreshObjectivesRef.current === "function"
      ) {
        refreshObjectivesRef.current?.();
      }
      if (
        tabId === "nominations" &&
        typeof refreshNominationsRef.current === "function"
      ) {
        refreshNominationsRef.current?.();
      }
    });
  };

  // Handler for saving nomination draft with refresh
  const handleSaveNominationDraft = async () => {
    if (!saveDraftNominationRef.current) return;

    try {
      // Call the save draft function
      await Promise.resolve(saveDraftNominationRef.current());

      // Refresh nominations after successful save
      if (typeof refreshNominationsRef.current === "function") {
        refreshNominationsRef.current();
      }
    } catch (error) {
      console.error("Failed to save nomination draft:", error);
    }
  };

  const handleSubmitClick = () => {
    setShowValidationErrors(true);
    if (!isFormValid) {
      // Scroll to validation summary
      setTimeout(() => {
        const validationSummary = document.querySelector(
          "[data-validation-summary]",
        );
        if (validationSummary) {
          validationSummary.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return;
    }

    if (hasUnsavedChanges && onSaveDraftOKR) {
      setIsSaveDraftPromptOpen(true);
      return;
    }

    setIsSubmitConfirmOpen(true);
  };

  const handleSaveDraftClick = () => {
    setShowValidationErrors(true);
    if (!isFormValid) {
      // Scroll to validation summary
      setTimeout(() => {
        const validationSummary = document.querySelector(
          "[data-validation-summary]",
        );
        if (validationSummary) {
          validationSummary.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return;
    }

    if (onSaveDraftOKR) {
      onSaveDraftOKR();
    }
  };

  // Auto-fill employee email when report dialog opens
  useEffect(() => {
    if (isReportDialogOpen && user?.email) {
      setReportEmployeeEmail(user.email);
      // Auto-fill manager email from immediate manager if available
      if (immediateManagerEmail) {
        setReportManagerEmail(immediateManagerEmail);
      }
    }
  }, [isReportDialogOpen, user?.email, immediateManagerEmail]);

  const handleSaveDraftAndContinue = async () => {
    if (!onSaveDraftOKR) {
      setIsSaveDraftPromptOpen(false);
      setIsSubmitConfirmOpen(true);
      return;
    }

    try {
      setIsSavingBeforeSubmit(true);
      await Promise.resolve(onSaveDraftOKR());
      setIsSaveDraftPromptOpen(false);
      setIsSubmitConfirmOpen(true);
    } catch (error) {
      console.error("Failed to save draft before submission:", error);
    } finally {
      setIsSavingBeforeSubmit(false);
    }
  };

  // Keep activeTab in sync when location changes (browser navigation)
  useEffect(() => {
    const isNominationRoute =
      location.pathname.includes("/nominations") ||
      location.pathname.includes("/nomination");

    if (isNominationRoute) {
      if (hasNominationAccess) {
        setActiveTab("nominations");
      } else if (hasOKRAccess) {
        setActiveTab("objectives");
        navigate("/self-appraisal/okr-submission", { replace: true });
      }
    } else {
      if (hasOKRAccess) {
        setActiveTab("objectives");
      } else if (hasNominationAccess) {
        setActiveTab("nominations");
        navigate("/self-appraisal/nominations", { replace: true });
      }
    }
  }, [location.pathname, hasNominationAccess, hasOKRAccess, navigate]);

  useEffect(() => {
    if (activeTab === "objectives" && !hasOKRAccess) {
      if (hasNominationAccess) {
        setActiveTab("nominations");
        navigate("/self-appraisal/nominations", { replace: true });
      }
    } else if (activeTab === "nominations" && !hasNominationAccess) {
      if (hasOKRAccess) {
        setActiveTab("objectives");
        navigate("/self-appraisal/okr-submission", { replace: true });
      }
    }
  }, [activeTab, hasOKRAccess, hasNominationAccess, navigate]);

  const handleReportDialogClose = () => {
    if (isReportSubmitting) {
      return;
    }
    setIsReportDialogOpen(false);
    setReportRemarks("");
    setReportEmployeeEmail("");
    setReportManagerEmail("");
    setReportRemarksError("");
  };

  const handleNomReportDialogClose = () => {
    if (isNomReportSubmitting) {
      return;
    }
    setIsNomReportOpen(false);
    setNomReportError("");
  };

  // Handle report issue submission
  const handleReportSubmit = async () => {
    const trimmedRemarks = reportRemarks.trim();
    const trimmedEmployeeEmail = reportEmployeeEmail.trim();
    const trimmedManagerEmail = reportManagerEmail.trim();

    if (!trimmedRemarks) {
      setReportRemarksError(
        "Please describe the hierarchy issue before submitting.",
      );
      return;
    }

    if (
      !trimmedEmployeeEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmployeeEmail)
    ) {
      setReportRemarksError("Please provide a valid employee email address.");
      return;
    }

    if (
      !trimmedManagerEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedManagerEmail)
    ) {
      setReportRemarksError("Please provide a valid manager email address.");
      return;
    }

    setReportRemarksError("");
    setIsReportSubmitting(true);
    try {
      const response = await employeeService.reportIssueWithType(
        "hierarchy",
        trimmedRemarks,
        trimmedEmployeeEmail,
        trimmedManagerEmail,
      );

      if (response.status) {
        console.log("Issue reported successfully:", response.message);
        setIsReportDialogOpen(false);
        setReportRemarksError("");
        setReportRemarks("");
        setReportEmployeeEmail("");
        setReportManagerEmail("");
        setShowReportSuccess(true);
      } else {
        console.error("Failed to report issue:", response.error);
        setReportRemarksError("Failed to submit report. Please try again.");
      }
    } catch (error) {
      console.error("Error reporting issue:", error);
      setReportRemarksError("An error occurred. Please try again.");
    } finally {
      setIsReportSubmitting(false);
    }
  };

  // Handle nominations report issue submission
  const handleNomReportSubmit = async () => {
    const trimmedRemarks = nomReportRemarks.trim();

    if (nomReportSelected.length === 0 && !trimmedRemarks) {
      setNomReportError(
        "Please select incorrect hierarchy and add remarks before submitting.",
      );
      return;
    }

    if (nomReportSelected.length === 0) {
      setNomReportError("Please select which hierarchy is incorrect.");
      return;
    }

    if (!trimmedRemarks) {
      setNomReportError("Please add remarks to describe the issue.");
      return;
    }

    setNomReportError("");
    setIsNomReportSubmitting(true);
    try {
      // Combine selected hierarchies and remarks into a single message
      let remarksText = "";
      if (nomReportSelected.length > 0) {
        remarksText += `Incorrect hierarchy: ${nomReportSelected.join(", ")}. `;
      }
      if (trimmedRemarks) {
        remarksText += trimmedRemarks;
      }

      const response = await employeeService.reportIssueWithType(
        "hierarchy",
        remarksText,
      );

      if (response.status) {
        console.log(
          "Nomination issue reported successfully:",
          response.message,
        );
        setIsNomReportOpen(false);
        setNomReportError("");
        setNomReportRemarks("");
        setNomReportSelected([]);
        setShowNomReportSuccess(true);
      } else {
        console.error("Failed to report nomination issue:", response.error);
        // You can add an error notification here if needed
      }
    } catch (error) {
      console.error("Error reporting nomination issue:", error);
      // You can add an error notification here if needed
    } finally {
      setIsNomReportSubmitting(false);
    }
  };

  useEffect(() => {
    if (!validationErrors || validationErrors.length === 0) {
      setShowValidationErrors(false);
    }
  }, [validationErrors]);

  // Show API error modal when API error message is received
  useEffect(() => {
    if (apiErrorMessage) {
      setIsApiErrorModalOpen(true);
    }
  }, [apiErrorMessage]);

  // Determine if deadlines have passed separately for objectives and nominations
  // Allow actions on the final day (daysLeft === 0). Consider passed only when daysLeft < 0.
  // DEADLINE CHECK DISABLED - Always allow submissions
  const objectivesDeadlinePassed = false; // typeof daysLeft === 'number' && daysLeft < 0;
  const nominationsDeadlinePassed = false; // typeof nominationDaysLeft === 'number' && nominationDaysLeft < 0;

  // Helper function to render deadline warning message
  const renderDeadlineWarning = (
    deadlinePassed: boolean,
    type: "Objectives" | "Nominations",
  ) => {
    if (!deadlinePassed) return null;
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg py-4 px-6 mb-4">
        <div className="flex items-center space-x-2">
          <svg
            className="w-5 h-5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium text-red-800">
            {type} Deadline Passed
          </span>
        </div>
        <p className="text-red-700 text-sm mt-1">
          The deadline for {type.toLowerCase()} has passed. All fields and
          submission are now disabled.
        </p>
      </div>
    );
  };

  // Group validation messages per OKR into single lines
  const formatOkrListWithAnd = React.useCallback((numbers: number[]) => {
    if (numbers.length === 0) return "";
    if (numbers.length === 1) return `OKR ${numbers[0]}`;
    if (numbers.length === 2) return `OKR ${numbers[0]} and ${numbers[1]}`;
    const initial = numbers
      .slice(0, -1)
      .map((n) => n.toString())
      .join(", ");
    const last = numbers[numbers.length - 1];
    return `OKR ${initial}, and ${last}`;
  }, []);

  const groupedValidationLines = React.useMemo(() => {
    if (!validationErrors || !validationErrors.length) return [] as string[];

    const duplicateSet = new Set<number>();
    const missingSet = new Set<number>();
    const ratingMessages: string[] = [];
    const otherMessages: string[] = [];
    const okrRegex = /^OKR\s*(\d+):\s*(.*)$/i;

    validationErrors.forEach((entry) => {
      const raw = String(entry);
      const match = raw.match(okrRegex);

      if (!match) {
        otherMessages.push(raw);
        return;
      }

      const okrNumber = Number(match[1]);
      const detail = match[2].trim();

      if (/duplicate objective/i.test(detail)) {
        duplicateSet.add(okrNumber);
        return;
      }

      if (
        /objective is required/i.test(detail) ||
        /target is required/i.test(detail) ||
        /(end|mid) year result is required/i.test(detail)
      ) {
        missingSet.add(okrNumber);
        return;
      }

      if (/rating/i.test(detail)) {
        ratingMessages.push(`OKR ${okrNumber}: ${detail.replace(/\.$/, "")}.`);
        return;
      }

      otherMessages.push(`OKR ${okrNumber}: ${detail}`);
    });

    const lines: string[] = [];

    if (duplicateSet.size > 0) {
      const numbers = Array.from(duplicateSet).sort((a, b) => a - b);
      const label = formatOkrListWithAnd(numbers);
      lines.push(`${label} are duplicates, please remove any one of them.`);
    }

    if (missingSet.size > 0) {
      const numbers = Array.from(missingSet).sort((a, b) => a - b);
      const slashList = numbers.map((n) => `OKR ${n}`).join("/");
      const requiredFieldsLabel = isEyrRatingLocked
        ? "Objective and Target"
        : `Objective, Target and the ${resultColumnLabel}`;
      lines.push(
        `Please fill the ${requiredFieldsLabel} for ${slashList} before submitting/saving.`,
      );
    }

    lines.push(...ratingMessages);
    lines.push(...otherMessages);

    return lines;
  }, [validationErrors, formatOkrListWithAnd, isEyrRatingLocked, resultColumnLabel]);

  function renderObjectivesTab() {
    return (
      <div className="space-y-4">
        {renderDeadlineWarning(objectivesDeadlinePassed, "Objectives")}

        {/* Unsaved Changes Warning */}
        {hasUnsavedChanges && !isFreezed && (
          <div className="mb-4 bg-yellow-50 border border-yellow-300 rounded-lg py-3 px-6">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <span className="font-medium text-yellow-800">
                You have unsaved changes
              </span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
              Please save your draft before submitting your OKRs.
            </p>
          </div>
        )}

        <div className="mx-1 flex flex-col gap-4 rounded-lg bg-purple-50 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="text-base font-medium text-gray-700">
              {cycleType === CycleType.END ? "Immediate Manager" : "Current Superior"}
            </span>
            <span className="text-sm font-medium text-gray-500 italic">
              (Your {cycleType === CycleType.END ? "immediate manager" : "current superior"} as of last 3 months)
            </span>
          </div>
          <div className="min-w-0">
            <span className="inline-block max-w-full break-words rounded-lg bg-purple-800 px-3 py-2 font-bold text-white">
              <NameWithEmailTooltip
                as="span"
                name={toTitleCase(displayImmediateManager) || "N/A"}
                email={immediateManagerEmail}
                className="text-white"
              />
            </span>
          </div>
        </div>

        {/* OKR Table */}
        <div className="table-container">
          {/* Table Header */}
          <div className="bg-primary-800 rounded-lg px-6 py-4 border-b border-gray-200 mb-3">
            <div className="grid grid-cols-12 gap-4 text-left text-base font-medium text-white tracking-wider">
              <div className="text-base col-span-1">SNO.</div>
              <div className="text-base col-span-3">Objective</div>
              <div className="text-base col-span-2">Target</div>
              <div className="text-base col-span-3 flex items-center gap-1.5">
                {resultColumnLabel}
                {isEyrRatingLocked && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 opacity-70 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="text-base col-span-2 flex items-center gap-1.5">
                Ratings
                {isEyrRatingLocked && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 opacity-70 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="text-base col-span-1"></div>
            </div>
          </div>

          {/* Table Body */}
          <div className="space-y-3 relative">
            {okrs.map((okr, index) => {
              const okrErrors = getOkrErrors(index);
              const hasErrors = okrErrors.length > 0;

              return (
                <div
                  key={okr.id}
                  data-okr-row={okr.id}
                  className={`hover:bg-gray-50 border ${hasErrors ? "border-red-300 bg-red-50" : "border-gray-200"} rounded-lg px-6 py-4 transition-colors relative focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-300 focus-within:outline-none`}
                >
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-1">
                      <div
                        className="flex items-center justify-center"
                        style={{ minHeight: "48px" }}
                      >
                        <span className="font-medium text-gray-700 text-base">
                          {index + 1}.
                        </span>
                      </div>
                    </div>
                    <div className="col-span-3">
                      <textarea
                        value={okr.objective}
                        onChange={(e) => {
                          if (isEditable && !objectivesDeadlinePassed) {
                            onOKRChange(okr.id, "objective", e.target.value);
                            e.target.style.height = "auto";
                            e.target.style.height =
                              e.target.scrollHeight + "px";
                          }
                        }}
                        id={`okr-objective-${okr.id}`}
                        rows={1}
                        disabled={!isEditable || objectivesDeadlinePassed}
                        data-auto-resize="true"
                        className={`w-full p-3 text-sm border ${hasErrors && !okr.objective.trim() ? "border-red-400" : "border-gray-300"} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 placeholder-gray-400 resize-none overflow-hidden transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed shadow-sm`}
                        placeholder="Enter objective here..."
                        style={{ minHeight: "48px", whiteSpace: "pre-wrap" }}
                      />
                    </div>
                    <div className="col-span-2">
                      <textarea
                        value={okr.target}
                        onChange={(e) => {
                          if (isEditable && !objectivesDeadlinePassed) {
                            onOKRChange(okr.id, "target", e.target.value);
                            e.target.style.height = "auto";
                            e.target.style.height =
                              e.target.scrollHeight + "px";
                          }
                        }}
                        id={`okr-target-${okr.id}`}
                        rows={1}
                        disabled={!isEditable || objectivesDeadlinePassed}
                        data-auto-resize="true"
                        className={`w-full p-3 text-sm border ${hasErrors && !okr.target.trim() ? "border-red-400" : "border-gray-300"} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 placeholder-gray-400 resize-none overflow-hidden transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed shadow-sm`}
                        placeholder="Enter target here..."
                        style={{ minHeight: "48px", whiteSpace: "pre-wrap" }}
                      />
                    </div>
                    {isEyrRatingLocked ? (
                      <Tooltip content={eyrRatingLockedTooltip} className="col-span-3">
                        <div className="col-span-3">
                      <textarea
                        value={okr.endYearResult}
                        onChange={(e) => {
                          if (isEditable && !objectivesDeadlinePassed && !isEyrRatingLocked) {
                            onOKRChange(
                              okr.id,
                              "endYearResult",
                              e.target.value,
                            );
                            e.target.style.height = "auto";
                            e.target.style.height =
                              e.target.scrollHeight + "px";
                          }
                        }}
                        id={`okr-endYearResult-${okr.id}`}
                        rows={1}
                        
                        disabled={!isEditable || objectivesDeadlinePassed || isEyrRatingLocked}
                        data-auto-resize="true"
                        className={`w-full p-3 text-sm border ${hasErrors && !okr.endYearResult.trim() ? "border-red-400" : "border-gray-300"} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 placeholder-gray-400 resize-none overflow-hidden transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed shadow-sm`}
                        placeholder={ "Enter your result here..."}
                        style={{ minHeight: "48px", whiteSpace: "pre-wrap" }}
                      />
                        </div>
                      </Tooltip>
                    ) : (
                      <div className="col-span-3">
                      <textarea
                        value={okr.endYearResult}
                        onChange={(e) => {
                          if (isEditable && !objectivesDeadlinePassed && !isEyrRatingLocked) {
                            onOKRChange(
                              okr.id,
                              "endYearResult",
                              e.target.value,
                            );
                            e.target.style.height = "auto";
                            e.target.style.height =
                              e.target.scrollHeight + "px";
                          }
                        }}
                        id={`okr-endYearResult-${okr.id}`}
                        rows={1}
                        
                        disabled={!isEditable || objectivesDeadlinePassed || isEyrRatingLocked}
                        data-auto-resize="true"
                        className={`w-full p-3 text-sm border ${hasErrors && !okr.endYearResult.trim() ? "border-red-400" : "border-gray-300"} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 placeholder-gray-400 resize-none overflow-hidden transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed shadow-sm`}
                        placeholder={ "Enter your result here..."}
                        style={{ minHeight: "48px", whiteSpace: "pre-wrap" }}
                      />
                      </div>
                    )}
                    {isEyrRatingLocked ? (
                      <Tooltip content={eyrRatingLockedTooltip} className="col-span-2">
                        <div className="col-span-2">
                      <select
                        value={okr.rating ?? ""}
                        onChange={(e) => {
                          if (isEditable && !objectivesDeadlinePassed && !isEyrRatingLocked) {
                            const nextValue =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            onOKRChange(okr.id, "rating", nextValue);
                          }
                        }}
                        id={`okr-rating-${okr.id}`}
                        disabled={!isEditable || objectivesDeadlinePassed || isEyrRatingLocked}
                        className={`w-full p-3 text-sm border ${hasErrors && okr.rating === null ? "border-red-400" : "border-gray-300"} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed shadow-sm`}
                        style={{ height: "48px" }}
                      >
                        <option value="">Select</option>
                        <option value={1}>1-Excel</option>
                        <option value={2}>2-Fully Met</option>
                        <option value={3}>3-Mostly Met</option>
                        <option value={4}>4-Requires Action</option>
                      </select>
                        </div>
                      </Tooltip>
                    ) : (
                      <div className="col-span-2">
                      <select
                        value={okr.rating ?? ""}
                        onChange={(e) => {
                          if (isEditable && !objectivesDeadlinePassed && !isEyrRatingLocked) {
                            const nextValue =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            onOKRChange(okr.id, "rating", nextValue);
                          }
                        }}
                        id={`okr-rating-${okr.id}`}
                        disabled={!isEditable || objectivesDeadlinePassed || isEyrRatingLocked}
                        className={`w-full p-3 text-sm border ${hasErrors && okr.rating === null ? "border-red-400" : "border-gray-300"} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed shadow-sm`}
                        style={{ height: "48px" }}
                      >
                        <option value="">Select</option>
                        <option value={1}>1-Excel</option>
                        <option value={2}>2-Fully Met</option>
                        <option value={3}>3-Mostly Met</option>
                        <option value={4}>4-Requires Action</option>
                      </select>
                      </div>
                    )}
                    <div className="col-span-1">
                      <div
                        className="flex items-center justify-center"
                        style={{ minHeight: "48px" }}
                      >
                        {okrs.length > 1 &&
                          isEditable &&
                          !objectivesDeadlinePassed && (
                            <button
                              onClick={() => onRemoveOKR(okr.id)}
                              className="flex items-center justify-center w-10 h-10 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-200 flex-shrink-0 hover:shadow-md"
                              title="Remove OKR"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8"
                                />
                              </svg>
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Validation summary */}
        {!isFormValid &&
          showValidationErrors &&
          groupedValidationLines.length > 0 && (
            <div
              data-validation-summary
              className="p-4 bg-red-50 rounded-lg border border-red-100"
              role="alert"
              aria-live="polite"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-red-700 text-sm font-medium">
                    Please resolve following issues before proceeding to
                    save/submit the OKRs
                  </p>
                  <div className="mt-2 grid gap-2">
                    {groupedValidationLines.map((line, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                          {i + 1}
                        </span>
                        <span className="text-sm text-red-700">{line}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* OKR Status Messages - Show above buttons */}
        {/* Show approved message when is_freezed is true and status is approved */}
        {isFreezed && okrStatus === "approved" && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg py-4 px-6">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-medium text-green-800">
                OKR Self-Appraisal Approved
              </span>
            </div>
            <p className="text-green-700 text-sm mt-1">
              Your OKRs have been approved by your manager for the {cycleType !== undefined ? (cycleType === 0 ? 'Target Settings' : cycleType === 1 ? 'Mid Year Review' : 'End Year') : 'current'} cycle.
            </p>
          </div>
        )}

        {/* Show rejected message when is_freezed is false and there's a comment */}
        {!isFreezed && okrComments && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg py-4 px-6">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <span className="font-medium text-red-800">
                OKR Self-Appraisal Rejected
              </span>
            </div>
            <p className="text-red-700 text-sm mt-1">
              Reason: <span className="font-semibold">{okrComments}</span>
            </p>
            <p className="text-gray-600 text-xs mt-3">
              You can update your OKRs and resubmit.
            </p>
          </div>
        )}

        {/* Show submitted message when is_freezed is true but status is not approved (pending review) */}
        {isFreezed && okrStatus !== "approved" && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg py-4 px-6">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-medium text-yellow-800">
                OKRs Submitted - Pending Review
              </span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
              Your OKRs have been submitted successfully and are awaiting
              manager review.
            </p>
          </div>
        )}

        {!isFreezed  && okrStatus !== "pemding" && (
          <div className="flex flex-col gap-4 border-t border-gray-200 bg-white px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            {objectivesDeadlinePassed ? (
              <span className="text-gray-400 text-sm cursor-not-allowed">
                Report Issue in Hierarchy?
              </span>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsReportDialogOpen(true);
                }}
                className="text-red-500 text-sm hover:underline"
              >
                Report Issue in Hierarchy?
              </a>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {onSaveDraftOKR && isEditable && (
                <Button
                  variant="outline"
                  onClick={handleSaveDraftClick}
                  disabled={objectivesDeadlinePassed || isSaving}
                  className="text-purple-600 border-purple-600 hover:bg-purple-50 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    !canSaveDraft
                      ? "Save Draft not available - check debug info"
                      : objectivesDeadlinePassed
                        ? "Objectives deadline has passed"
                        : "Save Draft"
                  }
                >
                  {isSaving ? "Saving..." : "Save Draft"}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  onAddOKR();
                }}
                disabled={!isEditable || objectivesDeadlinePassed}
                className="text-purple-600 border-purple-600 hover:bg-purple-50 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  objectivesDeadlinePassed
                    ? "Objectives deadline has passed"
                    : "Add more OKRs"
                }
              >
                Add more OKRs
              </Button>

              <Tooltip
                content={
                  hasUnsavedChanges
                    ? "You have unsaved changes. Please save your draft before submitting."
                    : objectivesDeadlinePassed
                      ? "Objectives deadline has passed"
                      : !draftExists
                        ? "Please save a draft first"
                        : "Submit your OKRs"
                }
              >
                <Button
                  onClick={handleSubmitClick}
                  disabled={
                    hasUnsavedChanges ||
                    !canSaveDraft ||
                    isSubmitting ||
                    !isEditable ||
                    objectivesDeadlinePassed ||
                    !draftExists
                  }
                  className="bg-purple-800 hover:bg-purple-700 text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Tooltip>
            </div>
          </div>
        )}

        {/* Report Success Dialog */}
        <Dialog
          isOpen={showReportSuccess}
          onClose={() => setShowReportSuccess(false)}
          type="success"
          title="Report Submitted"
          message="Your report has been submitted successfully. Our team will verify your query and revert back to you as soon as possible."
          confirmText="OK"
          onConfirm={() => setShowReportSuccess(false)}
          showCancel={false}
          size="extended"
        />

        <Dialog
          isOpen={isReportDialogOpen}
          onClose={handleReportDialogClose}
          title="Report Issue in Hierarchy"
          size="extended"
          message={
            <div>
              <p className="text-gray-700 mb-4">
                We’re sorry to hear that you’re experiencing an issue with your
                reporting hierarchy. Please specify which part of the hierarchy
                is incorrect, and share your email ID along with your correct
                reporting manager’s email ID. This will help us resolve the
                issue quickly and accurately.
              </p>
              {/* Employee Email Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Email
                </label>
                <input
                  type="email"
                  value={reportEmployeeEmail}
                  readOnly
                  placeholder="your.email@company.com"
                  className="w-full border rounded p-3 text-sm text-gray-700 placeholder-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed"
                />
              </div>

              {/* Manager Email Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Correct Manager's Email
                </label>
                <input
                  type="email"
                  value={reportManagerEmail}
                  onChange={(e) => {
                    setReportManagerEmail(e.target.value);
                    if (reportRemarksError) setReportRemarksError("");
                  }}
                  placeholder="manager.email@company.com"
                  className="w-full border rounded p-3 text-sm text-gray-700 placeholder-gray-400 border-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  disabled={isReportSubmitting}
                />
              </div>
              <textarea
                value={reportRemarks}
                onChange={(e) => {
                  const value = e.target.value;
                  setReportRemarks(value);
                  if (reportRemarksError && value.trim()) {
                    setReportRemarksError("");
                  }
                }}
                placeholder="Add your remarks"
                className={`w-full border rounded p-3 mb-2 text-sm text-gray-700 placeholder-gray-400 ${
                  reportRemarksError
                    ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-gray-200"
                }`}
                rows={3}
                disabled={isReportSubmitting}
                aria-invalid={reportRemarksError ? "true" : "false"}
              />
              {reportRemarksError && (
                <p className="text-red-600 text-sm mb-2">
                  {reportRemarksError}
                </p>
              )}
            </div>
          }
          type="info"
          showCancel={false}
          customActions={
            <>
              <button
                onClick={handleReportDialogClose}
                disabled={isReportSubmitting}
                className="text-red-500 underline text-sm px-4 py-2 bg-transparent rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleReportSubmit}
                disabled={
                  isReportSubmitting ||
                  !reportRemarks.trim() ||
                  !reportEmployeeEmail.trim() ||
                  !reportManagerEmail.trim()
                }
                className="bg-purple-800 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isReportSubmitting ? "Submitting..." : "Submit"}
              </button>
            </>
          }
        />

        <Dialog
          isOpen={isSaveDraftPromptOpen}
          onClose={() => {
            if (!isSavingBeforeSubmit) setIsSaveDraftPromptOpen(false);
          }}
          title="Save Draft Required"
          message={
            <div>
              <p className="text-gray-800 mb-3 font-medium">
                Submitted OKRs do not match draft version.
              </p>
              <p className="text-gray-700 text-sm">
                First save the draft before submitting.
              </p>
            </div>
          }
          type="error"
          showCancel={false}
          customActions={
            <>
              <button
                onClick={() => {
                  if (!isSavingBeforeSubmit) setIsSaveDraftPromptOpen(false);
                }}
                className="text-red-500 underline text-sm px-4 py-2 bg-transparent rounded disabled:opacity-50"
                disabled={isSavingBeforeSubmit}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDraftAndContinue}
                className="bg-purple-800 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSavingBeforeSubmit}
              >
                {isSavingBeforeSubmit ? "Saving..." : "Save Draft & Continue"}
              </button>
            </>
          }
        />

        {/* API Error Modal - for submission errors from API */}
        <Dialog
          isOpen={isApiErrorModalOpen}
          onClose={() => setIsApiErrorModalOpen(false)}
          title={apiErrorTitle || "Submission Error"}
          message={
            <div>
              <p className="text-gray-800 mb-3">{apiErrorMessage}</p>
            </div>
          }
          type="error"
          showCancel={false}
          customActions={
            <>
              <button
                onClick={() => setIsApiErrorModalOpen(false)}
                className="bg-purple-800 text-white px-4 py-2 rounded-lg"
              >
                OK
              </button>
            </>
          }
        />

        <Dialog
          isOpen={isSubmitConfirmOpen}
          onClose={() => setIsSubmitConfirmOpen(false)}
          title="Have You Had a Conversation with Your Superior?"
          message={
            <div>
              <p className="text-gray-800 mb-3 font-medium">
                You're about to submit your Objectives and Key Results (OKRs).
              </p>
              <p className="text-gray-700 mb-4">
                By proceeding, you confirm that you have discussed your
                performance and OKRs with your superior.
              </p>
              <p className="text-gray-700">
                Once submitted, your OKRs will be locked and you will no longer
                be able to make edits. Please review your objectives, targets
                and results before confirming.
              </p>
            </div>
          }
          type="warning"
          showCancel={false}
          customActions={
            <>
              <button
                onClick={() => setIsSubmitConfirmOpen(false)}
                className="text-red-500 underline text-sm px-4 py-2 bg-transparent rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsSubmitConfirmOpen(false);
                  onSubmit();
                }}
                className="bg-purple-800 text-white px-4 py-2 rounded-lg"
              >
                Yes, submit and lock my OKRs
              </button>
            </>
          }
        />
      </div>
    );
  }

  // Show rejected reason modal if manager comment is available and status is draft
  // Removed useEffect for rejected reason modal

  // Filter peers to exclude stakeholders
  const filteredPeers = React.useMemo(() => {
    if (!availableNominations?.peers) return [];

    // Get IDs of selected stakeholders
    const selectedStakeholderIds = new Set(
      nominations.stakeholders.map((stakeholder) => stakeholder.id),
    );
    const selectedStakeholderEmails = new Set(
      nominations.stakeholders
        .map((stakeholder) => stakeholder.email)
        .filter(Boolean),
    );

    // Filter out peers that are selected as stakeholders
    return availableNominations.peers.filter((peer) => {
      const isStakeholder =
        selectedStakeholderIds.has(peer.id) ||
        (peer.email && selectedStakeholderEmails.has(peer.email));
      return !isStakeholder;
    });
  }, [availableNominations?.peers, nominations.stakeholders]);

  // Filter stakeholders to exclude peers
  const filteredStakeholders = React.useMemo(() => {
    if (!availableNominations?.stakeholders) return [];

    // Get IDs of selected peers
    const selectedPeerIds = new Set(nominations.peers.map((peer) => peer.id));
    const selectedPeerEmails = new Set(
      nominations.peers.map((peer) => peer.email).filter(Boolean),
    );

    // Filter out stakeholders that are selected as peers
    return availableNominations.stakeholders.filter((stakeholder) => {
      const isPeer =
        selectedPeerIds.has(stakeholder.id) ||
        (stakeholder.email && selectedPeerEmails.has(stakeholder.email));
      return !isPeer;
    });
  }, [availableNominations?.stakeholders, nominations.peers]);

  const allNominationOptions = React.useMemo(
    () => [
      ...(availableNominations?.dotted_superior ?? []),
      ...(availableNominations?.subordinates ?? []),
      ...(availableNominations?.additional_subordinates ?? []),
      ...(availableNominations?.peers ?? []),
      ...(availableNominations?.stakeholders ?? []),
    ],
    [availableNominations],
  );
  const renderNominationsTab = () => (
    <div className="relative space-y-6">
      {/* Loading overlay when refreshing */}
      {isNominationRefreshing && (
        <div className="absolute inset-0 bg-white bg-opacity-90 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="text-purple-600 font-medium">
              Refreshing nominations...
            </span>
          </div>
        </div>
      )}

      {renderDeadlineWarning(nominationsDeadlinePassed, "Nominations")}

      <div className="flex flex-col gap-4 rounded-lg bg-purple-50 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span className="text-base font-medium text-gray-700">
            Nomination Approver
          </span>
          <span className="text-sm font-medium text-gray-500 italic">
            (Your immediate superior as of last 3 months)
          </span>
        </div>
        <div className="min-w-0">
          <span className="inline-block max-w-full break-words rounded-lg bg-purple-800 px-3 py-2 font-bold text-white">
            <NameWithEmailTooltip
              as="span"
              name={displayNominationApprover || "N/A"}
              email={displayNominationApproverEmail}
              className="text-white"
            />
          </span>
        </div>
      </div>

      <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
        <div>
          <div className="mb-3">
            <span className="font-medium text-gray-700">Superiors</span>
            <span className="text-red-500 ml-1">*</span>
            <span className="text-xs text-gray-500 italic ml-2">
              (Immediate superiors that you have been working with for the
              current year - Auto-populated from system)
            </span>
          </div>
          {nominations.superiors.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {nominations.superiors.map((superior, index) => {
                const displayName = superior.full_name;

                return (
                  <span
                    key={index}
                    className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded text-base border border-purple-200 flex items-center"
                  >
                    <NameWithEmailTooltip
                      as="span"
                      name={displayName}
                      email={superior.email}
                      className=""
                    />
                    <svg
                      className="w-4 h-4 ml-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-gray-500 italic p-3 bg-gray-50 rounded-lg">
              No superiors found in system records
            </div>
          )}
        </div>
      </div>

      {/* Only show Additional Superiors card if there are available options */}
      {availableNominations?.dotted_superior &&
        availableNominations.dotted_superior.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
            <MultiSelectSearchField
              label="Additional Superiors"
              required={false}
              description="(Select colleagues who provide additional supervision.)"
              options={availableNominations.dotted_superior}
              emailOptions={allNominationOptions}
              excludedEmails={currentUserEmail ? [currentUserEmail] : []}
              selectedValues={nominations.dotted_superior}
              excludedValues={[
                ...nominations.superiors,
                ...nominations.additional_subordinates,
                ...nominations.subordinates,
                ...nominations.peers,
                ...nominations.stakeholders,
              ]}
              onSelectionChange={(values) =>
                onNominationChange("dotted_superior", values)
              }
              placeholder="Search and select additional superiors..."
              maxSelections={10}
              disabled={
                !isNominationEditable ||
                isNominationDisabled ||
                nominationsDeadlinePassed ||
                !(
                  nominationStatus === "draft" ||
                  nominationStatus === "rejected"
                )
              }
            />
          </div>
        )}

      {/* Only show Subordinates card if there are available subordinates */}
      {availableNominations?.subordinates &&
        availableNominations.subordinates.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
            <MultiSelectSearchField
              label="Subordinates"
              required={true}
              description="(1-10 of your direct report subordinates that you have been working with)"
              options={availableNominations.subordinates}
              emailOptions={allNominationOptions}
              allowExternalEmail={false}
              excludedEmails={currentUserEmail ? [currentUserEmail] : []}
              selectedValues={nominations.subordinates}
              excludedValues={[
                ...nominations.superiors,
                ...nominations.dotted_superior,
                ...nominations.additional_subordinates,
                ...nominations.peers,
                ...nominations.stakeholders,
              ]}
              onSelectionChange={(values) =>
                onNominationChange("subordinates", values)
              }
              placeholder="Search and select subordinates..."
              maxSelections={10}
              disabled={
                !isNominationEditable ||
                isNominationDisabled ||
                nominationsDeadlinePassed ||
                !(
                  nominationStatus === "draft" ||
                  nominationStatus === "rejected"
                )
              }
            />
          </div>
        )}

      {availableNominations?.additional_subordinates &&
        availableNominations.additional_subordinates.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
            <MultiSelectSearchField
              label="Additional Subordinates"
              required={false}
              description="(This is optional. 1-10 of your direct report subordinates that you have been working with in your previous role)"
              options={availableNominations.additional_subordinates}
              emailOptions={allNominationOptions}
              excludedEmails={currentUserEmail ? [currentUserEmail] : []}
              selectedValues={nominations.additional_subordinates}
              excludedValues={[
                ...nominations.superiors,
                ...nominations.dotted_superior,
                ...nominations.subordinates,
                ...nominations.peers,
                ...nominations.stakeholders,
              ]}
              onSelectionChange={(values) =>
                onNominationChange("additional_subordinates", values)
              }
              placeholder="Search and select additional subordinates..."
              maxSelections={10}
              disabled={
                !isNominationEditable ||
                isNominationDisabled ||
                nominationsDeadlinePassed ||
                !(
                  nominationStatus === "draft" ||
                  nominationStatus === "rejected"
                )
              }
            />
          </div>
        )}

      {/* Only show Peers card if there are available peers */}
      {availableNominations?.peers && availableNominations.peers.length > 0 && (
        <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
          <MultiSelectSearchField
            label="Peers"
            required={true}
            description="(1-10 peers that you have been working with for the current year)"
            options={filteredPeers}
            emailOptions={allNominationOptions}
            excludedEmails={currentUserEmail ? [currentUserEmail] : []}
            selectedValues={nominations.peers}
            excludedValues={[
              ...nominations.superiors,
              ...nominations.dotted_superior,
              ...nominations.subordinates,
              ...nominations.additional_subordinates,
              ...nominations.stakeholders,
            ]}
            onSelectionChange={(values) => onNominationChange("peers", values)}
            placeholder="Search and select peers..."
            maxSelections={10}
            disabled={
              !isNominationEditable ||
              isNominationDisabled ||
              nominationsDeadlinePassed ||
              !(nominationStatus === "draft" || nominationStatus === "rejected")
            }
          />
        </div>
      )}

      {/* Only show Stakeholders card if there are available stakeholders */}
      {availableNominations?.stakeholders &&
        availableNominations.stakeholders.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-lg p-6 space-y-6">
            <MultiSelectSearchField
              label="Stakeholders"
              required={false}
              description="(This is optional. Please restrict to a maximum of three (3) stakeholders)"
              options={filteredStakeholders}
              emailOptions={allNominationOptions}
              excludedEmails={currentUserEmail ? [currentUserEmail] : []}
              selectedValues={nominations.stakeholders}
              excludedValues={[
                ...nominations.superiors,
                ...nominations.dotted_superior,
                ...nominations.subordinates,
                ...nominations.additional_subordinates,
                ...nominations.peers,
              ]}
              onSelectionChange={(values) =>
                onNominationChange("stakeholders", values)
              }
              placeholder="Search and select stakeholders..."
              maxSelections={3}
              disabled={
                !isNominationEditable ||
                isNominationDisabled ||
                nominationsDeadlinePassed ||
                !(
                  nominationStatus === "draft" ||
                  nominationStatus === "rejected"
                )
              }
            />
          </div>
        )}

      {nominationStatus === "draft" && managerComments && (
        <div className="bg-red-50 border border-red-200 rounded-lg py-4 px-6">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <span className="font-medium text-red-800">
              Nomination Approval Rejected
            </span>
          </div>
          <p className="text-red-700 text-sm mt-1">
            Reason:{" "}
            <span className="font-semibold">
              {managerComments || "No reason provided"}
            </span>
          </p>
          <p className="text-gray-600 text-xs mt-3">
            You can update your nominations and resubmit.
          </p>
        </div>
      )}
      {nominationStatus === "approved" && (
        <div className="bg-green-50 border border-green-200 rounded-lg py-4 px-6">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium text-green-800">
              Nominations Approved
            </span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            Your nominations have been approved and can no longer be modified.
          </p>
        </div>
      )}

      {nominationStatus === "pending" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg py-4 px-6">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium text-amber-800">
              Nomination Approval Pending
            </span>
          </div>
          <p className="text-amber-700 text-sm mt-1">
            Your nominations have been submitted successfully. Please wait for
            approval from your manager.
          </p>
        </div>
      )}

      {nominationStatus === "draft" && (
        <div className="flex flex-col gap-4 border-t border-gray-200 bg-white px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          {nominationsDeadlinePassed ? (
            <span className="text-gray-400 text-sm cursor-not-allowed">
              Report Issue in Hierarchy?
            </span>
          ) : (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsNomReportOpen(true);
              }}
              className="text-red-500 text-sm hover:underline"
            >
              Report Issue in Hierarchy?
            </a>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {onSaveDraftNomination && isNominationEditable && (
              <Tooltip
                content={
                  hasUnsavedNominationChanges
                    ? "You have unsaved changes. Please save your draft before submitting."
                    : isNominationDisabled
                      ? "Nominations are approved and cannot be modified"
                      : nominationsDeadlinePassed
                        ? "Nominations deadline has passed"
                        : "Save Draft"
                }
              >
              <Button
                variant="outline"
                onClick={handleSaveNominationDraft}
                disabled={
                  isNominationDisabled ||
                  nominationsDeadlinePassed ||
                  isNominationRefreshing ||
                  nominationStatus !== "draft"
                }
                className="text-purple-600 border-purple-600 hover:bg-purple-50 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving
                  ? "Saving..."
                  : isNominationRefreshing
                    ? "Refreshing..."
                    : "Save Draft"}
              </Button>
              </Tooltip>
            )}
            <Tooltip
              content={
                hasUnsavedNominationChanges
                  ? "You have unsaved changes. Please save your draft before submitting."
                  : nominationsDeadlinePassed
                    ? "Nominations deadline has passed"
                    : nominationStatus !== "draft"
                      ? "Nominations can only be submitted in draft status"
                      : isNominationSubmitEnabled
                        ? "No nominations selected"
                        : "Submit"
              }
            >
            <Button
              onClick={() => setIsNomSubmitConfirmOpen(true)}
              disabled={
                isSubmitting ||
                !isNominationEditable ||
                nominationsDeadlinePassed ||
                isNominationSubmitEnabled ||
                nominationStatus !== "draft"
              }
              className="bg-purple-800 hover:bg-purple-700 text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Nominations Report Success Dialog */}
      <Dialog
        isOpen={showNomReportSuccess}
        onClose={() => setShowNomReportSuccess(false)}
        type="success"
        title="Report Submitted"
        message="Your report has been submitted successfully. Our team will verify your query and revert back to you as soon as possible."
        confirmText="OK"
        onConfirm={() => setShowNomReportSuccess(false)}
        showCancel={false}
        size="extended"
      />

      <Dialog
        isOpen={isNomReportOpen}
        onClose={handleNomReportDialogClose}
        title="Report Issue in Hierarchy"
        size="extended"
        message={
          <div>
            <p className="text-gray-700 mb-4">
              We’re sorry to hear that you’re experiencing an issue with your
              reporting hierarchy. Please specify which part of the hierarchy is
              incorrect, and share your email ID along with your correct
              reporting manager’s email ID. This will help us resolve the issue
              quickly and accurately.
            </p>
            <div className="mb-3">
              <select
                className={`w-full border rounded p-3 mb-3 text-sm ${
                  nomReportError && nomReportSelected.length === 0
                    ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-gray-200"
                }`}
                value={nomReportSelected[0] || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) {
                    return;
                  }
                  setNomReportSelected((prev) => {
                    if (prev.includes(v)) {
                      return prev;
                    }
                    const next = [...prev, v];
                    if (
                      nomReportError &&
                      next.length > 0 &&
                      nomReportRemarks.trim()
                    ) {
                      setNomReportError("");
                    }
                    return next;
                  });
                }}
                disabled={isNomReportSubmitting}
                aria-invalid={nomReportError ? "true" : "false"}
              >
                <option value="">Select incorrect hierarchy*</option>
                {hierarchyOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <div className="flex gap-2 mb-3">
                {nomReportSelected.map((sel) => (
                  <span
                    key={sel}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded border border-purple-300 text-purple-700 bg-white text-sm"
                  >
                    {sel}
                    <button
                      onClick={() => {
                        setNomReportSelected((prev) => {
                          const next = prev.filter((s) => s !== sel);
                          if (
                            nomReportError &&
                            next.length > 0 &&
                            nomReportRemarks.trim()
                          ) {
                            setNomReportError("");
                          }
                          return next;
                        });
                      }}
                      className="text-gray-500 hover:text-red-500"
                      disabled={isNomReportSubmitting}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <textarea
              value={nomReportRemarks}
              onChange={(e) => {
                const value = e.target.value;
                setNomReportRemarks(value);
                if (
                  nomReportError &&
                  value.trim() &&
                  nomReportSelected.length > 0
                ) {
                  setNomReportError("");
                }
              }}
              placeholder="Add your remarks"
              className={`w-full border rounded p-3 mb-2 text-sm text-gray-700 placeholder-gray-400 ${
                nomReportError &&
                (!nomReportRemarks.trim() || nomReportSelected.length === 0)
                  ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  : "border-gray-200"
              }`}
              rows={3}
              disabled={isNomReportSubmitting}
              aria-invalid={nomReportError ? "true" : "false"}
            />
            {nomReportError && (
              <p className="text-red-600 text-sm mb-2">{nomReportError}</p>
            )}
          </div>
        }
        type="info"
        showCancel={false}
        customActions={
          <>
            <button
              onClick={handleNomReportDialogClose}
              disabled={isNomReportSubmitting}
              className="text-red-500 underline text-sm px-4 py-2 bg-transparent rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleNomReportSubmit}
              disabled={
                isNomReportSubmitting ||
                nomReportSelected.length === 0 ||
                !nomReportRemarks.trim()
              }
              className="bg-purple-800 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isNomReportSubmitting ? "Submitting..." : "Submit"}
            </button>
          </>
        }
      />

      {/* Nomination submit confirmation dialog */}
      <Dialog
        isOpen={isNomSubmitConfirmOpen}
        onClose={() => setIsNomSubmitConfirmOpen(false)}
        title="Submit your Nominations"
        message={
          <div>
            <p className="text-gray-800 mb-3 font-medium">
              You're about to submit your nominations.
            </p>
            <p className="text-gray-700 mb-4">
              Once submitted, your nominations will be locked and you will no
              longer be able to make edits. Please review your selections before
              confirming.
            </p>
          </div>
        }
        type="warning"
        showCancel={false}
        customActions={
          <>
            <button
              onClick={() => setIsNomSubmitConfirmOpen(false)}
              className="text-red-500 underline text-sm px-4 py-2 bg-transparent rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setIsNomSubmitConfirmOpen(false);
                (onSubmitNomination || onSubmit)?.();
              }}
              className="bg-purple-800 text-white px-4 py-2 rounded-lg"
            >
              Yes, submit and lock my nominations
            </button>
          </>
        }
      />
    </div>
  );

  const activeContent = (() => {
    if (activeTab === "objectives" && hasOKRAccess) {
      return renderObjectivesTab();
    }
    if (activeTab === "nominations" && hasNominationAccess) {
      return renderNominationsTab();
    }
    if (hasOKRAccess) {
      return renderObjectivesTab();
    }
    if (hasNominationAccess) {
      return renderNominationsTab();
    }
    return (
      <div className="text-center py-12 text-gray-500">
        You do not have access to self appraisal content.
      </div>
    );
  })();

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  return (
    <div className="space-y-6">
      {tabs.length > 1 && (
        <div className="responsive-tabs flex gap-2 mx-1">
          {tabs.map((tab) => {
            const hasAccess = tabAccessMap[tab.id];
            return (
              <DisabledTabTooltip
                key={tab.id}
                disabled={!hasAccess}
                content="This feature is currently not available for this cycle."
              >
                <button
                  onClick={() => hasAccess && handleTabChange(tab.id)}
                  disabled={!hasAccess}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    !hasAccess
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50 "
                      : activeTab === tab.id
                        ? "bg-purple-100 text-purple-800 shadow-md outline outline-2 outline-purple-800"
                        : "text-gray-600 hover:bg-gray-200 cursor-pointer"
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              </DisabledTabTooltip>
            );
          })}
        </div>
      )}

      <div className="mt-6 ">{activeContent}</div>
    </div>
  );
};

export default SelfAppraisalPage;
